import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { loyaltyAPI } from '../utils/api';
import { useTheme, useMediaQuery } from '@mui/material';

function AdminLoyalty() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Config form state
  const [configForm, setConfigForm] = useState({
    stampsNeeded: 6,
    discountPercent: 50,
    active: true
  });

  // Adjustment modal state
  const [adjustModal, setAdjustModal] = useState({
    isOpen: false,
    customer: null,
    category: '',
    stamps: 1,
    reason: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, statsRes, customersRes] = await Promise.all([
        loyaltyAPI.getConfig(),
        loyaltyAPI.getStats(),
        loyaltyAPI.getCustomers()
      ]);

      setConfig(configRes.data.config);
      setCategories(configRes.data.categories || []);
      setConfigForm({
        stampsNeeded: configRes.data.config?.stampsNeeded || 6,
        discountPercent: configRes.data.config?.discountPercent || 50,
        active: configRes.data.config?.active ?? true
      });
      setStats(statsRes.data.stats);
      setCustomers(customersRes.data.customers || []);
    } catch (err) {
      console.error('Failed to fetch loyalty data:', err);
      toast.error('Failed to load loyalty data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await loyaltyAPI.updateConfig(configForm);
      toast.success('Settings saved successfully');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustStamps = async () => {
    if (!adjustModal.customer || !adjustModal.category) return;

    try {
      await loyaltyAPI.adjustStamps(adjustModal.customer.id, {
        stamps: adjustModal.stamps,
        category: adjustModal.category,
        reason: adjustModal.reason
      });
      toast.success(`Stamps ${adjustModal.stamps > 0 ? 'added' : 'deducted'} for ${adjustModal.category}`);
      setAdjustModal({ isOpen: false, customer: null, category: '', stamps: 1, reason: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to adjust stamps');
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get total stamps for a customer across categories or for selected category
  const getCustomerStampDisplay = (customer) => {
    if (selectedCategory === 'all') {
      const total = customer.categoryStamps?.reduce((sum, cs) => sum + cs.stamps, 0) || 0;
      return total;
    }
    const catStamps = customer.categoryStamps?.find(cs => cs.category === selectedCategory);
    return catStamps?.stamps || 0;
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="spinner"></div>
        <p>Loading loyalty data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <h1 style={{ marginBottom: '0.5rem' }}>Loyalty Program</h1>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>
        Manage your stamp card rewards program (per service category)
      </p>

      {/* Stats Cards */}
      <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="stat-card">
          <div className="title">Active Members</div>
          <div className="value">{stats?.totalMembers || 0}</div>
          <div className="subtitle">with stamps</div>
        </div>
        <div className="stat-card">
          <div className="title">Categories</div>
          <div className="value">{categories.length}</div>
          <div className="subtitle">service types</div>
        </div>
        <div className="stat-card">
          <div className="title">Active Codes</div>
          <div className="value">{stats?.activeDiscountCodes || 0}</div>
          <div className="subtitle">unredeemed</div>
        </div>
        <div className="stat-card">
          <div className="title">Redemptions</div>
          <div className="value">{stats?.redemptionsThisMonth || 0}</div>
          <div className="subtitle">this month</div>
        </div>
      </div>

      {/* Category Stats */}
      {stats?.byCategory && stats.byCategory.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>Stamps by Category</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
            {stats.byCategory.map(cat => (
              <div
                key={cat.category}
                style={{
                  background: '#f8f9fa',
                  padding: '1rem',
                  borderRadius: '8px',
                  minWidth: '150px'
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{cat.category}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#8B2635' }}>
                  {cat.total_stamps}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                  {cat.members} members
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr',
        gap: '1.5rem'
      }}>
        {/* Settings Panel */}
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Program Settings</h2>

          <form onSubmit={handleSaveConfig}>
            <div className="form-group">
              <label>Stamps Needed for Reward</label>
              <input
                type="number"
                min="1"
                max="20"
                value={configForm.stampsNeeded}
                onChange={(e) => setConfigForm({ ...configForm, stampsNeeded: parseInt(e.target.value) || 6 })}
              />
              <small style={{ color: '#666' }}>
                Per category - visits required to earn a discount
              </small>
            </div>

            <div className="form-group">
              <label>Discount Percentage</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="number"
                  min="5"
                  max="100"
                  step="5"
                  value={configForm.discountPercent}
                  onChange={(e) => setConfigForm({ ...configForm, discountPercent: parseInt(e.target.value) || 50 })}
                  style={{ width: '100px' }}
                />
                <span>%</span>
              </div>
              <small style={{ color: '#666' }}>
                Discount applied when reward is redeemed
              </small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={configForm.active}
                  onChange={(e) => setConfigForm({ ...configForm, active: e.target.checked })}
                  style={{ width: 'auto' }}
                />
                Program Active
              </label>
              <small style={{ color: '#666' }}>
                When disabled, no new stamps will be awarded
              </small>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              style={{ width: '100%' }}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </form>

          {/* How It Works */}
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0' }}>How It Works</h4>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', color: '#666' }}>
              <li>Stamps are tracked per service category (Brows, Nails, etc.)</li>
              <li>Earn 1 stamp per completed visit in that category</li>
              <li>After {configForm.stampsNeeded} stamps in a category, earn {configForm.discountPercent}% off</li>
              <li>Discount codes are category-specific</li>
              <li>Discounted visits don't count towards stamps</li>
              <li>Only 1 active code per category at a time</li>
            </ul>
          </div>

          {/* Categories List */}
          {categories.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem 0' }}>Service Categories</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {categories.map(cat => (
                  <span
                    key={cat}
                    style={{
                      background: '#8B2635',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '16px',
                      fontSize: '0.875rem'
                    }}
                  >
                    {cat}
                  </span>
                ))}
              </div>
              <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                Categories are based on services. Add a category to a service in Services management.
              </small>
            </div>
          )}
        </div>

        {/* Customers Panel */}
        <div className="card">
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            marginBottom: '1rem',
            gap: '0.75rem'
          }}>
            <h2 style={{ margin: 0 }}>Customer Stamps</h2>
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{ padding: '0.5rem', width: isMobile ? '100%' : 'auto' }}
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: isMobile ? '100%' : '150px' }}
              />
            </div>
          </div>

          {filteredCustomers.length === 0 ? (
            <p style={{ color: '#666', textAlign: 'center', padding: '2rem 0' }}>
              No customers found
            </p>
          ) : isMobile ? (
            /* Mobile Card View */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {filteredCustomers.map(customer => {
                const selectedCatStamps = customer.categoryStamps?.find(cs => cs.category === selectedCategory);
                return (
                  <div
                    key={customer.id}
                    style={{
                      background: '#f8f9fa',
                      borderRadius: '12px',
                      padding: '1rem',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <div>
                        <strong style={{ fontSize: '1rem' }}>{customer.name}</strong>
                        <div style={{ color: '#666', fontSize: '0.8rem' }}>{customer.email}</div>
                      </div>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: '#8B2635',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1.1rem'
                      }}>
                        {selectedCategory === 'all'
                          ? getCustomerStampDisplay(customer)
                          : (selectedCatStamps?.stamps || 0)
                        }
                      </span>
                    </div>

                    {selectedCategory === 'all' ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        {customer.categoryStamps?.filter(cs => cs.stamps > 0).map(cs => (
                          <span
                            key={cs.category}
                            style={{
                              background: '#e8e4e0',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '16px',
                              fontSize: '0.8rem'
                            }}
                          >
                            {cs.category}: {cs.stamps}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <span style={{ fontSize: '0.8rem', color: '#666' }}>Progress:</span>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {[...Array(configForm.stampsNeeded)].map((_, i) => (
                              <div
                                key={i}
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  background: i < ((selectedCatStamps?.stamps || 0) % configForm.stampsNeeded) ? '#D4A574' : '#e0e0e0'
                                }}
                              />
                            ))}
                          </div>
                          <span style={{ fontSize: '0.75rem', color: '#666' }}>
                            ({selectedCatStamps?.stampsToReward || configForm.stampsNeeded} to go)
                          </span>
                        </div>
                        {(selectedCatStamps?.rewardsAvailable || 0) > 0 && (
                          <span style={{
                            background: '#4CAF50',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {selectedCatStamps.rewardsAvailable} Reward{selectedCatStamps.rewardsAvailable > 1 ? 's' : ''} Available
                          </span>
                        )}
                      </div>
                    )}

                    <button
                      className="btn btn-secondary"
                      style={{ width: '100%', padding: '0.5rem' }}
                      onClick={() => setAdjustModal({
                        isOpen: true,
                        customer,
                        category: selectedCategory !== 'all' ? selectedCategory : (categories[0] || ''),
                        stamps: 1,
                        reason: ''
                      })}
                    >
                      Adjust Stamps
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop Table View */
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    {selectedCategory === 'all' ? (
                      <>
                        <th style={{ textAlign: 'center' }}>Total Stamps</th>
                        <th>By Category</th>
                      </>
                    ) : (
                      <>
                        <th style={{ textAlign: 'center' }}>Stamps</th>
                        <th style={{ textAlign: 'center' }}>Progress</th>
                        <th style={{ textAlign: 'center' }}>Rewards</th>
                      </>
                    )}
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map(customer => {
                    const selectedCatStamps = customer.categoryStamps?.find(cs => cs.category === selectedCategory);
                    return (
                      <tr key={customer.id}>
                        <td>
                          <div>
                            <strong>{customer.name}</strong>
                            <br />
                            <small style={{ color: '#666' }}>{customer.email}</small>
                          </div>
                        </td>
                        {selectedCategory === 'all' ? (
                          <>
                            <td style={{ textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                background: '#8B2635',
                                color: 'white',
                                fontWeight: 600
                              }}>
                                {getCustomerStampDisplay(customer)}
                              </span>
                            </td>
                            <td>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                {customer.categoryStamps?.filter(cs => cs.stamps > 0).map(cs => (
                                  <span
                                    key={cs.category}
                                    style={{
                                      background: '#f0f0f0',
                                      padding: '0.125rem 0.5rem',
                                      borderRadius: '12px',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    {cs.category}: {cs.stamps}
                                  </span>
                                ))}
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                background: '#8B2635',
                                color: 'white',
                                fontWeight: 600
                              }}>
                                {selectedCatStamps?.stamps || 0}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <div style={{ display: 'flex', justifyContent: 'center', gap: '3px' }}>
                                {[...Array(configForm.stampsNeeded)].map((_, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      width: '10px',
                                      height: '10px',
                                      borderRadius: '50%',
                                      background: i < ((selectedCatStamps?.stamps || 0) % configForm.stampsNeeded) ? '#D4A574' : '#e0e0e0'
                                    }}
                                  />
                                ))}
                              </div>
                              <small style={{ color: '#666' }}>
                                {selectedCatStamps?.stampsToReward || configForm.stampsNeeded} to go
                              </small>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {(selectedCatStamps?.rewardsAvailable || 0) > 0 && (
                                <span style={{
                                  background: '#4CAF50',
                                  color: 'white',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: 600
                                }}>
                                  {selectedCatStamps.rewardsAvailable} Available
                                </span>
                              )}
                            </td>
                          </>
                        )}
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                            onClick={() => setAdjustModal({
                              isOpen: true,
                              customer,
                              category: selectedCategory !== 'all' ? selectedCategory : (categories[0] || ''),
                              stamps: 1,
                              reason: ''
                            })}
                          >
                            Adjust
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Adjustment Modal */}
      {adjustModal.isOpen && (
        <div className="modal-overlay" onClick={() => setAdjustModal({ ...adjustModal, isOpen: false })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Adjust Stamps</h2>
              <button
                className="modal-close-btn"
                onClick={() => setAdjustModal({ ...adjustModal, isOpen: false })}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Adjusting stamps for <strong>{adjustModal.customer?.name}</strong>
              </p>

              <div className="form-group">
                <label>Category</label>
                <select
                  value={adjustModal.category}
                  onChange={(e) => setAdjustModal({ ...adjustModal, category: e.target.value })}
                >
                  <option value="">Select category...</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {adjustModal.category && (
                <p style={{ color: '#666', fontSize: '0.875rem' }}>
                  Current {adjustModal.category} stamps:{' '}
                  {adjustModal.customer?.categoryStamps?.find(cs => cs.category === adjustModal.category)?.stamps || 0}
                </p>
              )}

              <div className="form-group">
                <label>Stamps to Add/Remove</label>
                <input
                  type="number"
                  value={adjustModal.stamps}
                  onChange={(e) => setAdjustModal({ ...adjustModal, stamps: parseInt(e.target.value) || 0 })}
                />
                <small style={{ color: '#666' }}>
                  Use negative number to remove stamps
                </small>
              </div>

              <div className="form-group">
                <label>Reason (optional)</label>
                <input
                  type="text"
                  placeholder="e.g., Correction, Goodwill gesture"
                  value={adjustModal.reason}
                  onChange={(e) => setAdjustModal({ ...adjustModal, reason: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setAdjustModal({ ...adjustModal, isOpen: false })}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAdjustStamps}
                disabled={!adjustModal.stamps || !adjustModal.category}
              >
                {adjustModal.stamps > 0 ? 'Add' : 'Remove'} Stamps
              </button>
            </div>
          </div>{/* modal-content */}
        </div>
      )}
    </div>
  );
}

export default AdminLoyalty;
